import { useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";

const FORM_ADDR = "https://readdy.ai/api/form/db88nngkk00l6eltrhag";

const highlights = [
  {
    icon: "ri-book-open-line",
    title: "剧情梳理",
    text: "按话数整理剧情脉络，标注分集亮点与隐藏伏笔，帮助观众建立清晰的作品时间线。",
  },
  {
    icon: "ri-user-heart-line",
    title: "角色解析",
    text: "从人物简介、性格特质到关键剧情与综合评价，多维度还原每一个角色的动机。",
  },
  {
    icon: "ri-newspaper-line",
    title: "资讯追踪",
    text: "汇总最新制作消息、活动动态与访谈内容，让爱好者不错过任何一条重要更新。",
  },
];

export default function AboutPage() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      form.reset();
      return;
    }

    setStatus("loading");
    setFormError("");

    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "company_alt") return;
        const str = String(value).trim();
        if (str) payload.append(key, str);
      });

      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = String(
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || ""
      );
      const isSpam = serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试。");
    }
  };

  return (
    <SiteLayout>
      <div className="border-b border-background-200 bg-secondary-50">
        <div className="mx-auto w-full max-w-7xl px-4 pt-28 pb-10 md:px-8 md:pt-36 md:pb-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于本站" }]} />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
            关于本站
          </h1>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
            一个以「家庭教师」动画为核心、面向广泛动漫爱好者的剧情介绍与角色解析平台。
          </p>
        </div>
      </div>

      <div className="mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="min-w-0">
            <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                网站定位
              </h2>
              <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-[15px] md:leading-8">
                「家庭教师」动画剧情介绍与角色解析平台，致力于为动漫爱好者提供一份清晰、系统、可反复查阅的作品资料库。
                我们围绕剧情、角色与资讯三条主线组织内容，既有面向新观众的入门梳理，也有面向深度爱好者的细节考据。
              </p>
              <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-[15px] md:leading-8">
                目标用户包括正在追番的新朋友、喜欢深入解读剧情的资深观众，以及希望随时掌握相关动态的广泛动漫受众。
                我们希望这里既有足够的信息密度，也保有一份舒适的阅读节奏。
              </p>
            </section>

            <section className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
              {highlights.map((h) => (
                <div
                  key={h.title}
                  className="rounded-lg border border-background-200 bg-background-50 p-5"
                >
                  <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                    <i className={`${h.icon} text-xl`} aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 font-heading text-sm font-bold text-foreground-950">
                    {h.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-500">{h.text}</p>
                </div>
              ))}
            </section>

            <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
              <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
                使用说明与版权声明
              </h2>
              <ul className="mt-4 space-y-3 text-sm leading-7 text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-1 text-primary-500" aria-hidden="true" />
                  站内所有剧情、角色与资讯内容均为演示虚拟数据，用于展示信息结构与浏览体验。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-1 text-primary-500" aria-hidden="true" />
                  本站不提供任何影片播放、下载、注册或登录功能，也不涉及真实作品版权信息与评分。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-1 text-primary-500" aria-hidden="true" />
                  收藏功能仅保存在你当前使用的浏览器中，不会上传到任何服务器，清除浏览器数据后可能丢失。
                </li>
                <li className="flex gap-2">
                  <i className="ri-checkbox-circle-line mt-1 text-primary-500" aria-hidden="true" />
                  如需引用站内内容用于学习交流，请注明来源；请勿用于任何商业用途。
                </li>
              </ul>
            </section>
          </div>

          <aside className="lg:sticky lg:top-28 lg:self-start">
            <div className="rounded-lg border border-background-200 bg-background-50 p-6">
              <h2 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-mail-send-line text-accent-500" aria-hidden="true" />
                意见反馈
              </h2>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                对内容或页面有任何建议，欢迎留言告诉我们。我们会认真阅读每一条反馈。
              </p>

              {status === "success" ? (
                <div className="mt-5 rounded-md border border-primary-200 bg-primary-50 px-4 py-6 text-center">
                  <i className="ri-checkbox-circle-line text-3xl text-primary-600" aria-hidden="true" />
                  <p className="mt-2 text-sm font-medium text-foreground-900">感谢你的反馈！</p>
                  <p className="mt-1 text-xs text-foreground-500">我们已收到你的留言。</p>
                  <button
                    type="button"
                    onClick={() => setStatus("idle")}
                    className="mt-4 text-xs font-medium text-primary-600 hover:text-primary-700"
                  >
                    继续留言
                  </button>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} className="mt-5 space-y-4">
                  <div>
                    <label
                      htmlFor="about-name"
                      className="mb-1.5 block text-xs font-medium text-foreground-600"
                    >
                      称呼
                    </label>
                    <input
                      id="about-name"
                      type="text"
                      name="name"
                      required
                      placeholder="请输入你的称呼"
                      className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="about-message"
                      className="mb-1.5 block text-xs font-medium text-foreground-600"
                    >
                      留言内容
                    </label>
                    <textarea
                      id="about-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="请填写你的建议或想说的话（最多 500 字）"
                      className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
                    />
                  </div>
                  <input
                    type="text"
                    name="company_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="aux-offset-guard"
                  />

                  {status === "error" && formError && (
                    <p className="rounded-md bg-accent-50 px-3 py-2 text-xs text-accent-800">
                      {formError}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === "loading"}
                    className="flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
                  >
                    <i
                      className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"}
                      aria-hidden="true"
                    />
                    {status === "loading" ? "提交中…" : "提交留言"}
                  </button>
                  <p className="text-center text-[11px] text-foreground-400">
                    表单仅用于接收反馈，不会用于其他用途。
                  </p>
                </form>
              )}
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}